      {/* ── the video, on paper — one capture, two things to send ────── */}
      <Reveal as="section" className="osv-band osv-light">
        <div className="osv-video">

        
          <VideoBlock />
          <div className="words">
            <h3>{video.headline}</h3>
            <p>{video.body}</p>
          </div>
        </div>
      </Reveal>

import React, { useRef, useState } from "react";
import { audiences, clients, close, film, home, how, platform, projectTypes, TAGLINE, video } from "./content";
import Reveal from "./Reveal";
import Icon from "./Icon";

/* The home page.

   Order is the founder's: the film first, the theatre second. The metaphor
   organises the page but is never written on it — builders buy outcomes, not
   analogies. Grounds alternate dark / paper / dark so the page stops reading
   as one black wall. */

/* The poster sits dimmed behind our own play button. Native controls only
   appear once the video is running — a default control bar sitting idle on a
   dark page is louder than anything else in the section. */
function VideoBlock() {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  return (
    <figure className={`player${playing ? " playing" : ""}`}>
      <video
        ref={ref}
        src={video.src}
        poster={video.poster}
        playsInline
        preload="metadata"
        controls={playing}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      {!playing && (
        <button
          type="button"
          className="play"
          aria-label="Play the project video"
          onClick={() => ref.current?.play()}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path d="M9 6.5l9 5.5-9 5.5z" fill="currentColor" />
          </svg>
        </button>
      )}
    </figure>
  );
}

export default function Home() {
  return (
    <>
      {/* ── hero ─────────────────────────────────────────────────────── */}
      <section className="osv-hero illus bleed">
        <figure className="osv-illus">
          <img src={home.image} alt="A buyer in a VR headset seeing a tower complete while only its base is built, with the platform around it" />
          {home.heroLabels.map((l) => (
            <a key={l.t} className="cap" href="#platform"
               style={{ left: `${l.x}%`, top: `${l.y}%` }}>{l.t}</a>
          ))}
        </figure>
        <div className="lay">
          <div className="eyebrow">{home.eyebrow}</div>
          <h1>{home.headline}</h1>
          <p className="lede">{home.lede}</p>
          <div className="acts">
            <a className="btn" href="#talk">{home.primary}</a>
            <a className="btn ghost" href="#how">{home.secondary}</a>
          </div>
        </div>
      </section>

      {/* ── the logo wall ────────────────────────────────────────────── */}
      <section className="osv-wall" aria-label="Builders already using OpenSkyVR">
        <p className="kicker">Trusted by</p>
        <div className="track">
          <ul>
            {/* Four passes, not two. The animation runs to -50%, so HALF the
                track must always be wider than the viewport or a gap opens
                after the last logo before the set comes round again. */}
            {[...clients, ...clients, ...clients, ...clients].map((c, i) => (
              <li key={`${c.name}-${i}`} aria-hidden={i >= clients.length}>
                <button type="button" className="logo" aria-label={c.name}
                  onClick={(e) => e.currentTarget.classList.toggle("on")}>
                  <img src={c.logo} alt={i < clients.length ? c.name : ""} style={{ height: c.h }} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── the walkthrough, and its four parts ──────────────────────── */}
      <Reveal as="section" className="osv-band rule" id="film">
        <div className="eyebrow">{film.eyebrow}</div>
        <div className="osv-lead-pair">
          <div>
            <h2>{film.headline}</h2>
            <p className="said">{film.said}</p>
          </div>
          <p className="lede">{film.body}</p>
        </div>

        <div className="osv-scenes">
          {film.scenes.map((s, i) => (
            <Reveal key={s.k} delay={i * 50}>
              <div className="osv-scene">
                <div className="art">
                  <img src={s.art} alt="" style={{ transform: `scale(${s.scale})` }} />
                </div>
                <div className="n">{String(i + 1).padStart(2, "0")}</div>
                <div className="k">{s.k}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>

      {/* ── the video, on paper — one capture, two things to send ────── */}
      <Reveal as="section" className="osv-band osv-light">
        <div className="osv-video">
          <VideoBlock />
          <div className="words">
            <h3>{video.headline}</h3>
            <p>{video.body}</p>
          </div>
        </div>
      </Reveal>

      {/* ── what we cover: five project types, on the dark ground ────── */}
      <Reveal as="section" className="osv-band rule">
        <div className="eyebrow">{projectTypes.eyebrow}</div>
        <div className="osv-lead-pair">
          <h2>{projectTypes.headline}</h2>
          <p className="lede">{projectTypes.body}</p>
        </div>

        <div className="osv-types">
          {projectTypes.items.map((t, i) => (
            <Reveal key={t.k} delay={i * 40} className="osv-type">
              <figure>
                <div className="shot">
                  <img src={t.img} alt={t.t} />
                </div>
                <h3>{t.t}</h3>
                <p>{t.d}</p>
                <div className="meta">{t.k}</div>
              </figure>
            </Reveal>
          ))}

          {/* The sixth cell is a question, not an invented sixth product.
              If plantation or farmland becomes a real line, it takes this
              slot with a drawing of its own. */}
          <Reveal delay={200} className="osv-type ask">
            <a href="#talk">
              <h3>{projectTypes.ask.t}</h3>
              <p>{projectTypes.ask.d}</p>
              <span className="go">{projectTypes.ask.cta}</span>
            </a>
          </Reveal>
        </div>
      </Reveal>

      {/* ── the theatre: eleven capabilities ─────────────────────────── */}
      <Reveal as="section" className="osv-band osv-deep" id="platform">
        <div className="eyebrow">{platform.eyebrow}</div>
        <div className="osv-lead-pair">
          <div>
            <h2>{platform.headline}</h2>
            <p className="said">{platform.said}</p>
          </div>
          <p className="lede">{platform.body}</p>
        </div>

        <div className="osv-features">
          {platform.features.map((f, i) => (
            <Reveal key={f.k} delay={Math.min(i, 6) * 32}>
              <div className="osv-feature">
                <span className="ic"><Icon name={f.icon} /></span>
                <div>
                  <h3>{f.k}</h3>
                  <p>{f.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="eyebrow osv-more">
          <span className="dot" /> and more being built
        </div>
      </Reveal>

      {/* ── how it works ─────────────────────────────────────────────── */}
      <Reveal as="section" className="osv-band osv-light" id="how">
        <div className="eyebrow">{how.eyebrow}</div>
        <h2 style={{ marginTop: 16 }}>{how.headline}</h2>
        <div className="osv-steps">
          {how.steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 60}>
              <div className="osv-step">
                <div className="n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>

      {/* ── who it's for ─────────────────────────────────────────────── */}
      <Reveal as="section" className="osv-band" id="who" style={{ paddingBottom: 0 }}>
        <div className="eyebrow">{audiences.eyebrow}</div>
        <h2 style={{ marginTop: 16, marginBottom: "clamp(26px,4vh,40px)" }}>{audiences.headline}</h2>
      </Reveal>
      <div className="osv-doors">
        {audiences.items.map((a, i) => (
          <Reveal key={a.k} delay={i * 45}>
            <div className="osv-door">
              <div className="no">{a.k}</div>
              <h3>{a.t}</h3>
              <p>{a.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* ── the one ask, and the sign-off ────────────────────────────── */}
      <Reveal as="section" className="osv-band osv-close" id="talk">
        <div className="eyebrow">{close.eyebrow}</div>
        <h2 style={{ marginTop: 16 }}>{close.headline}</h2>
        <p className="lede" style={{ marginTop: 18 }}>{close.body}</p>
        <div className="acts">
          <a className="btn" href={`mailto:${close.email}`}>{close.primary}</a>
        </div>
        <p className="osv-signoff said">{TAGLINE}</p>
      </Reveal>
    </>
  );
}
